import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import SearchInputComponent from "../../../../components/searchInput/searchInput";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { EventDetailDTO, GraduadaRowDTO, GraduadasDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Chip, dinero, normalizar } from "./comun";
import VentanaGraduada from "./ventanaGraduada";
import "../enrollmentRequests/filtrosMatriculas.css";

interface Props {
	campusId: number;
	evento: EventDetailDTO;
	grad: GraduadasDTO | null;
	soloLectura: boolean;
	reload: () => void;
	notify: (t: string, ok?: boolean) => void;
	/** Las pestañas del detalle van en la misma fila que el buscador (como Promociones). */
	tabs: React.ReactNode;
}

interface Filtros { pago: string; invitacion: string; curso: string; }
const VACIOS: Filtros = { pago: "", invitacion: "", curso: "" };

/** Pastilla de un solo valor, con las piezas de la barra de Matrículas (waFm*). */
const Pastilla: React.FC<{ titulo: string; opciones: { id: string; name: string }[]; valor: string; onChange: (v: string) => void }> = ({ titulo, opciones, valor, onChange }) => {
	const elegida = opciones.find(o => o.id === valor);
	return (
		<Dropdown className="waFm">
			<Dropdown.Toggle as="button" type="button" className={"waFmBoton" + (valor ? " activo" : "")}>
				<span className="waFmTitulo">{titulo}</span>
				{valor ? <span className="waFmValor">{elegida?.name || ""}</span> : null}
				<FontAwesomeIcon icon={solid("chevron-down")} className="waFmFlecha" />
			</Dropdown.Toggle>
			<Dropdown.Menu className="waFmMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
				<div className="waFmLista">
					{opciones.length < 1 ? <div className="waFmVacio">{S.sinResultados}</div> : opciones.map(o => (
						<Dropdown.Item as="button" type="button" key={o.id} className={"waFmOpcion" + (o.id === valor ? " elegida" : "")} onClick={() => onChange(o.id === valor ? "" : o.id)}>
							<span className="waFmNombre">{o.name}</span>
							{o.id === valor ? <FontAwesomeIcon icon={solid("check")} style={{ color: "var(--wa-acento)" }} /> : null}
						</Dropdown.Item>
					))}
				</div>
			</Dropdown.Menu>
		</Dropdown>
	);
};

export const invitadosTexto = (r: GraduadaRowDTO, tiers: number[]): string =>
	r.adults === 0 && r.children === 0 ? S.sinInvitados
		: (r.adults ? (tiers.includes(r.adults) ? S.paqueteN(r.adults) : S.nInvitados(r.adults)) : "") + (r.children ? (r.adults ? " " : "") + S.masNinos(r.children) : "");

export const PagoChip: React.FC<{ r: GraduadaRowDTO; cur: any }> = ({ r, cur }) => (
	<>{r.charged <= 0.004 && r.pending <= 0.004 ? <Chip>{S.sinCobro}</Chip> : r.fullyPaid ? <Chip tono="acento">{S.pagadoCompleto}</Chip> : <Chip tono="oro">{S.falta(dinero(cur, r.pending))}</Chip>}
		{r.extrasPending > 0.004 ? <div className="wevOro" style={{ fontSize: 12.5 }}>{S.extraFalta(dinero(cur, r.extrasPending))}</div> : null}</>
);

export const InvitacionChip: React.FC<{ r: GraduadaRowDTO }> = ({ r }) => (
	<Chip tono={r.invitation === "sinTelefono" || r.invitation === "faltaWhatsapp" ? "oro" : "filo"}>{S.invitacion[r.invitation]}</Chip>
);

/**
 * Graduadas: una fila por graduada (o participante) con sus invitados agrupados. Pago, invitación y entrada salen del servidor
 * (_graduadas). El clic abre su ventana con invitados, extras, historial y botones.
 */
const GraduadasTab: React.FC<Props> = ({ campusId, evento, grad, soloLectura, reload, notify, tabs }) => {
	const movil = useEsTelefono();
	const [q, setQ] = useState<string>("");
	const [buscar, setBuscar] = useState<string>("");
	const [f, setF] = useState<Filtros>(VACIOS);
	const [abierta, setAbierta] = useState<number | null>(null);
	const tiers = (evento.guestTiers || []).map(t => t.guests);
	const rows = useMemo(() => grad?.rows || [], [grad]);
	const cursos = useMemo(() => Array.from(new Set(rows.map(r => cursoCorto(r.course) || r.course || "").filter(Boolean))).sort((a, b) => a.localeCompare(b, "es")).map(c => ({ id: c, name: c })), [rows]);
	const filas = useMemo(() => {
		const t = normalizar(buscar);
		return rows.filter(r => (!f.pago || (f.pago === "completo" ? r.fullyPaid : !r.fullyPaid))
			&& (!f.invitacion || r.invitation === f.invitacion)
			&& (!f.curso || (cursoCorto(r.course) || r.course) === f.curso)
			&& (!t || normalizar([r.name, r.code, r.course].join(" ")).includes(t)));
	}, [rows, f, buscar]);
	const hay = !!(f.pago || f.invitacion || f.curso);
	const fila = abierta !== null ? rows.find(r => r.hostAttendeeId === abierta) || null : null;
	const entrada = (r: GraduadaRowDTO) => r.checkedIn ? S.nDeM(r.checkedIn, r.members) : <span className="wevSub">-</span>;
	const invitados = (r: GraduadaRowDTO) => <>{invitadosTexto(r, tiers)}{r.pendingNames ? <div className="wevOro" style={{ fontSize: 12.5 }}>{S.faltanNombres(r.pendingNames)}</div> : null}</>;
	const graduada = (r: GraduadaRowDTO) => <div style={{ minWidth: 0 }}><b>{r.name}</b>
		<div className="wevSub">{[cursoCorto(r.course) || r.course, r.kind === "participante" ? S.participante : null].filter(Boolean).join(" · ") || r.code || "-"}</div></div>;

	return (
		<div>
			{/* Escritorio: pestañas, pastillas y buscador en una sola fila (cabe más gente en 1280×720); teléfono: uno debajo del otro */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
				{tabs}
				<div className="waFmBarraListas" style={movil ? { flex: "1 1 100%", order: 3 } : { marginLeft: "auto" }}>
					<Pastilla titulo={S.filtroPago} opciones={[{ id: "completo", name: S.pagos.completo }, { id: "falta", name: S.pagos.falta }]} valor={f.pago} onChange={v => setF({ ...f, pago: v })} />
					<Pastilla titulo={S.filtroInvitacion} opciones={["enviada", "faltaWhatsapp", "sinEnviar", "sinTelefono"].map(k => ({ id: k, name: S.invitacion[k] }))} valor={f.invitacion} onChange={v => setF({ ...f, invitacion: v })} />
					<Pastilla titulo={S.filtroCurso} opciones={cursos} valor={f.curso} onChange={v => setF({ ...f, curso: v })} />
					{hay ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => setF(VACIOS)}>{S.limpiar}</button> : null}
				</div>
				<div style={movil ? { flex: "1 1 100%", position: "relative" } : { width: 260, position: "relative" }}>
					<SearchInputComponent search={q} onChangeSearch={setQ} onClickSearch={() => setBuscar(q.trim())} onClearSearch={() => { setQ(""); setBuscar(""); }} />
				</div>
			</div>
			<div className="waScrollX" style={{ overflowX: "auto", marginTop: 10 }}>
				{grad === null ? <div className="loader" /> : movil ? (
					<div className="wevTarjetas">
						{filas.length === 0 ? <div className="wevVacio">{rows.length ? S.sinResultados : S.sinGraduadas}</div> : filas.map(r => (
							<div key={r.hostAttendeeId} className="wevTarjeta" role="button" tabIndex={0} onClick={() => setAbierta(r.hostAttendeeId)} onKeyDown={e => { if (e.key === "Enter") setAbierta(r.hostAttendeeId); }}>
								<div className="arriba">{graduada(r)}<span className="wevSub" style={{ whiteSpace: "nowrap" }}>{r.checkedIn ? S.colEntrada + " " + S.nDeM(r.checkedIn, r.members) : ""}</span></div>
								<div style={{ fontSize: 14, marginTop: 4 }}>{invitados(r)}</div>
								<div className="chips"><PagoChip r={r} cur={evento.currency} /><InvitacionChip r={r} /></div>
							</div>
						))}
					</div>
				) : (
					<table className="table table-hover wevLista">
						<thead><tr>
							<th>{S.colGraduada}</th><th style={{ width: "18%" }}>{S.colInvitados}</th><th style={{ width: "20%" }}>{S.colPago}</th>
							<th style={{ width: "18%" }}>{S.colInvitacion}</th><th className="wevNum" style={{ width: 90 }}>{S.colEntrada}</th>
						</tr></thead>
						<tbody>
							{filas.length === 0 ? <tr><td colSpan={5} className="wevVacio">{rows.length ? S.sinResultados : S.sinGraduadas}</td></tr> : filas.map(r => (
								<tr key={r.hostAttendeeId} onClick={() => setAbierta(r.hostAttendeeId)}>
									<td>{graduada(r)}</td>
									<td>{invitados(r)}</td>
									<td><PagoChip r={r} cur={evento.currency} /></td>
									<td><InvitacionChip r={r} /></td>
									<td className="wevNum">{entrada(r)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
			{fila ? <VentanaGraduada campusId={campusId} evento={evento} fila={fila} soloLectura={soloLectura} onClose={() => setAbierta(null)} reload={reload} notify={notify} /> : null}
		</div>
	);
};

export default GraduadasTab;
